import { format } from 'date-fns'
import { type RCSContent, Template } from '../types'
import Iframe from './Iframe'
import './Preview.css'
import { useContext, useEffect, useState } from 'react'
import { ProjectContext } from '../contexts'
import JsonPreview from './JsonPreview'
import clsx from 'clsx'
import Heading from './Heading'
import { ArrowTopRightIcon, DesktopIcon, MapPinIcon, MobileIcon, PersonIcon, PhoneIcon } from './icons'
import RadioInput from './form/RadioInput'

interface PreviewProps {
    template: Pick<Template, 'type' | 'data'> & Partial<Pick<Template, 'locale'>>
    response?: any
    size?: 'small' | 'large'
}

function EmailPreview({ data, size, device }: { data: Record<string, any>, size: string, device: 'mobile' | 'desktop' }) {
    return (
        <div className={clsx('email-frame', `email-device-${device}`)}>
            <Iframe content={data.html ?? ''} allowScroll={size !== 'small'} />
        </div>
    )
}

function TextPreview({ data, locale, textMode }: { data: Record<string, any>, locale?: string, textMode: 'rcs' | 'default' }) {
    const [project] = useContext(ProjectContext)
    const rcs = data.rcs as RCSContent | undefined
    const card = rcs?.rich_card?.card_content
    const orientation = rcs?.rich_card?.card_orientation ?? 'VERTICAL'
    const imageAlignment = rcs?.rich_card?.thumbnail_image_alignment ?? 'LEFT'
    const showRcs = textMode === 'rcs'
    const isHorizontal = orientation === 'HORIZONTAL'

    return (
        <div className="text-frame phone-frame">
            <div className="text-frame-header">
                <div className="text-frame-profile-image">
                    <PersonIcon />
                </div>
            </div>
            <span className="text-frame-context">Text Message<br/>Today { format(new Date(), 'p') }</span>
            {showRcs && card && (
                <div className={clsx('rcs-message', isHorizontal && 'rcs-horizontal', isHorizontal && `rcs-image-${imageAlignment.toLowerCase()}`)}>
                    {card.media?.file_url && (
                        <img className={clsx('rcs-card-image', !isHorizontal && `rcs-card-image-${(card.media.height ?? 'MEDIUM').toLowerCase()}`)} src={card.media.file_url} alt={card.title ?? ''} />
                    )}
                    <div className="rcs-card-body">
                        {card.title && <span className="rcs-card-title">{card.title}</span>}
                        {card.description && <span className="rcs-card-description">{card.description}</span>}
                    </div>
                    {rcs?.suggestions && rcs.suggestions.length > 0 && (
                        <div className="rcs-suggestions">
                            {rcs.suggestions.map((s, i) => (
                                <button key={i} className="rcs-suggestion-chip">
                                    {s.type === 'url' && <ArrowTopRightIcon />}
                                    {s.type === 'dial' && <PhoneIcon />}
                                    {s.type === 'location' && <MapPinIcon />}
                                    {s.text}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {!(showRcs && card) && (
                <div className="text-bubble">{data.text}<br />{project.text_opt_out?.first?.[locale ?? 'en']}</div>
            )}
        </div>
    )
}

function PushPreview({ data }: { data: Record<string, any> }) {
    return (
        <div className="push-frame phone-frame">
            <div className="push-notification">
                <div className="notification-icon"></div>
                <div className="notification-header">
                    <span className="notification-title">{data.title}</span>
                    <span className="notification-time">now</span>
                </div>
                <span className="notification-body">{data.body}</span>
            </div>
        </div>
    )
}

function WebhookPreview({ data, response }: { data: Record<string, any>, response?: any }) {
    return (
        <div className="webhook-frame">
            <div className="webhook-block">
                <Heading title="Request" size="h5" />
                <JsonPreview value={data} />
            </div>
            {response && <div className="webhook-block">
                <Heading title="Response" size="h5" />
                <JsonPreview value={response.data} />
            </div>}
        </div>
    )
}

function InAppPreview({ data }: { data: Record<string, any> }) {
    return (
        <div className="in-app-frame">
            <div className="in-app-frame-phone">
                <Iframe content={data.html ?? ''} />
            </div>
        </div>
    )
}

export default function Preview({ template, response, size = 'large' }: PreviewProps) {
    const { data, type } = template

    const [device, setDevice] = useState<'mobile' | 'desktop'>('desktop')
    const [textMode, setTextMode] = useState<'rcs' | 'default'>('default')

    const hasRcsData = data.editor === 'interactive' && !!data.rcs?.rich_card?.card_content
    const showToolbar = size !== 'small' && (type === 'email' || (type === 'text' && hasRcsData))

    useEffect(() => {
        setTextMode(hasRcsData ? 'rcs' : 'default')
    }, [hasRcsData])

    let preview = null
    if (type === 'email') {
        preview = <EmailPreview data={data} size={size} device={device} />
    } else if (type === 'text') {
        preview = <TextPreview data={data} locale={template.locale} textMode={textMode} />
    } else if (type === 'push') {
        preview = <PushPreview data={data} />
    } else if (type === 'webhook') {
        preview = <WebhookPreview data={data} response={response} />
    } else if (type === 'in_app') {
        preview = <InAppPreview data={data} />
    }

    return (
        <section className={clsx('preview', size)}>
            {showToolbar && (
                <div className="preview-toolbar">
                    {type === 'email' && data.from?.address && (
                        <div className="preview-toolbar-info">
                            <span className="preview-toolbar-from">{data.from?.name} &lt;{data.from?.address}&gt;</span>
                            <span className="preview-toolbar-subject">{data.subject}</span>
                        </div>
                    )}
                    <div className="preview-toolbar-controls">
                        {type === 'email' && (
                            <RadioInput
                                options={[{
                                    key: 'desktop',
                                    label: <DesktopIcon />,
                                }, {
                                    key: 'mobile',
                                    label: <MobileIcon />,
                                }]}
                                size="small"
                                value={device}
                                onChange={(value) => setDevice(value)}
                            />
                        )}
                        {type === 'text' && hasRcsData && (
                            <RadioInput
                                options={[{
                                    key: 'rcs',
                                    label: 'Default',
                                }, {
                                    key: 'default',
                                    label: 'Fallback',
                                }]}
                                size="small"
                                value={textMode}
                                onChange={(value) => setTextMode(value)}
                            />
                        )}
                    </div>
                </div>
            )}
            <div className="preview-content">
                {preview}
            </div>
        </section>
    )
}
